import { enqueueSnackbar } from "notistack";
import {
  Chat,
  ChatState,
  createChat,
  deleteChat,
  deleteChats,
  getChatById,
  getChats,
  updateChat,
  updateChatsReadStatus,
  useChatStore,
} from "@/entities/chat";
import { searchChats } from "@/entities/search/api";
import { updateContact } from "@/entities/contact";
import { getLastLocalMessage } from "@/entities/message/lib";

// Тип для контекста (чтобы не передавать set/get напрямую)
type SetState = (
  partial:
    | ChatState
    | Partial<ChatState>
    | ((state: ChatState) => Partial<ChatState> | ChatState)
) => void;

type ChatContext = {
  getState: () => ChatState;
  setState: SetState;
};

export const handleFetchChats = async (
  context: ChatContext,
  isAdmin = false
): Promise<void> => {
  const { setState } = context;
  setState({ loading: true });

  try {
    const res = await getChats();
    if (res.success) {
      const enrichedChats = await Promise.all(
        res.result.map(async (chat) => {
          const localLastMsg = await getLastLocalMessage(chat._id);

          if (!localLastMsg) {
            return chat;
          }

          const serverLastMsg = chat.lastMessage;
          const localTime =
            typeof localLastMsg.createdAt === "string"
              ? new Date(localLastMsg.createdAt).getTime()
              : localLastMsg.createdAt;

          const serverTime = serverLastMsg
            ? typeof serverLastMsg.createdAt === "string"
              ? new Date(serverLastMsg.createdAt).getTime()
              : serverLastMsg.createdAt
            : -Infinity;

          if (localTime > serverTime) {
            return {
              ...chat,
              lastMessage: localLastMsg,
            };
          }

          return chat;
        })
      );

      setState({
        chats: enrichedChats,
        page: 2,
        count: res.count || 0,
        loading: false,
      });
    } else {
      enqueueSnackbar("Не удалось загрузить чаты", { variant: "error" });
      setState({ loading: false });
    }
  } catch (e) {
    console.error("Ошибка при загрузке чатов:", e);
    enqueueSnackbar("Ошибка при загрузке чатов", { variant: "error" });
    setState({ loading: false });
  }
};

export const handleLoadMoreChats = async (
  context: ChatContext
): Promise<void> => {
  const { getState, setState } = context;
  const state = getState();

  if (state.loadingMore || state.chats.length >= state.count) return;

  setState({ loadingMore: true });

  try {
    const nextPage = state.page;
    const res = await getChats(nextPage);

    if (res.success) {
      setState({
        chats: [...state.chats, ...res.result],
        page: nextPage + 1,
        loadingMore: false,
      });
    } else {
      setState({ loadingMore: false });
      enqueueSnackbar("Не удалось загрузить больше чатов", { variant: "info" });
    }
  } catch (e) {
    console.error("Ошибка при подгрузке чатов:", e);
    enqueueSnackbar("Ошибка при подгрузке чатов", { variant: "error" });
    setState({ loadingMore: false });
  }
};

export const handleSearchChatsWithPagination = async (
  context: ChatContext,
  query: string,
  reset = false
): Promise<void> => {
  const { getState, setState } = context;
  const pageToLoad = reset ? 1 : getState().page;

  setState({
    loading: reset,
    loadingMore: !reset,
    searchQuery: query,
  });

  try {
    const res = await searchChats({ query, page: pageToLoad });

    if (res.success) {
      const newChats = reset
        ? res.result
        : [...getState().chats, ...res.result];

      setState({
        chats: newChats,
        page: pageToLoad + 1,
        count: res.count || 0,
        loading: false,
        loadingMore: false,
      });

      if (reset && newChats.length > 0) {
        setState({ currentChat: newChats[0] });
      }
    } else {
      setState({
        chats: [],
        count: 0,
        loading: false,
        loadingMore: false,
      });
      enqueueSnackbar("Не удалось найти чаты", { variant: "info" });
    }
  } catch (e) {
    console.error(e);
    enqueueSnackbar("Ошибка поиска", { variant: "error" });
    setState({ loading: false, loadingMore: false });
  }
};

export const handleAddNewChat = async (
  context: ChatContext,
  newContact: { firstName: string; lastName: string; phone: string }
): Promise<Chat> => {
  const { getState, setState } = context;

  try {
    const res = await createChat(newContact);
    if (res.success) {
      const newChat = res.result;
      setState({
        chats: [...getState().chats, newChat],
        currentChat: newChat,
      });
      enqueueSnackbar("Чат создан", { variant: "success" });
      return newChat;
    } else {
      enqueueSnackbar("Не удалось создать чат", { variant: "error" });
      throw new Error("Failed to create chat");
    }
  } catch (e) {
    enqueueSnackbar("Ошибка при создании чата", { variant: "error" });
    throw e;
  }
};

export const handleUpdateChat = async (
  context: ChatContext,
  chatId: string,
  name: string
): Promise<void> => {
  const { getState, setState } = context;

  setState((state) => ({
    loadingChatIds: { ...state.loadingChatIds, [chatId]: true },
  }));

  try {
    const res = await updateChat(chatId, name);
    if (res.success) {
      setState((state) => ({
        chats: state.chats.map((c) => (c._id === chatId ? res.result : c)),
      }));
      enqueueSnackbar("Чат обновлён", { variant: "success" });
    } else {
      enqueueSnackbar("Не удалось обновить чат", { variant: "error" });
    }
  } catch (e) {
    enqueueSnackbar("Ошибка при обновлении чата", { variant: "error" });
  } finally {
    setState((state) => {
      const { [chatId]: _, ...rest } = state.loadingChatIds;
      return { loadingChatIds: rest };
    });
  }
};

export const handleDeleteChats = async (
  context: ChatContext,
  chatIds: string[]
): Promise<void> => {
  const { getState, setState } = context;

  setState((state) => ({
    loadingChatIds: {
      ...state.loadingChatIds,
      ...chatIds.reduce((acc, chatId) => ({ ...acc, [chatId]: true }), {}),
    },
  }));

  try {
    const res = await deleteChats(chatIds);

    const deletedChats: Chat[] = res.result || [];
    const deletedIds = deletedChats.map((c) => c._id);

    const notDeletedIds = chatIds.filter((id) => !deletedIds.includes(id));

    setState((state) => {
      const newChats = state.chats.filter((c) => !deletedIds.includes(c._id));
      const shouldResetCurrentChat =
        state.currentChat && deletedIds.includes(state.currentChat._id);

      if (shouldResetCurrentChat) {
        const url = new URL(window.location.href);
        url.searchParams.delete('chat');
        window.history.replaceState(null, '', url.toString());
      }

      return {
        chats: newChats,
        currentChat: shouldResetCurrentChat ? null : state.currentChat,
        count: state.count - deletedIds.length,
      };
    });

    if (deletedIds.length > 0 && notDeletedIds.length === 0) {
      enqueueSnackbar("Чаты успешно удалены", { variant: "success" });
    } else if (deletedIds.length > 0 && notDeletedIds.length > 0) {
      const notDeletedNames = getState()
        .chats.filter((c) => notDeletedIds.includes(c._id))
        .map((c) => c.contact.firstName)
        .join(", ");

      enqueueSnackbar(`Не удалось удалить чаты: ${notDeletedNames}`, {
        variant: "warning",
      });
    } else {
      enqueueSnackbar("Не удалось удалить чаты", { variant: "error" });
    }
  } catch (e) {
    enqueueSnackbar("Ошибка при удалении чатов", { variant: "error" });
  } finally {
    setState((state) => {
      const newLoadingChatIds = Object.fromEntries(
        Object.entries(state.loadingChatIds).filter(
          ([id]) => !chatIds.includes(id)
        )
      );
      return { loadingChatIds: newLoadingChatIds };
    });
  }
};

export const handleLoadMore = async (context: ChatContext): Promise<void> => {
  const state = context.getState();
  if (state.loadingMore || state.chats.length >= state.count) return;

  if (state.searchQuery) {
    await handleSearchChatsWithPagination(context, state.searchQuery, false);
  } else {
    await handleLoadMoreChats(context);
  }
};

export const handleUpdateChatContact = async (
  context: ChatContext,
  chatId: string,
  firstName: string,
  lastName: string
): Promise<void> => {
  const { getState, setState } = context;

  const chat = getState().chats.find((c) => c._id === chatId);
  if (!chat) {
    console.error("Chat not found");
    enqueueSnackbar("Чат не найден", { variant: "error" });
    return;
  }

  const userId = chat.contact.user._id;

  try {
    const res = await updateContact(userId, firstName, lastName);
    if (res.success && res.result) {
      setState((state) => {
        const updatedChats = state.chats.map((c) =>
          c._id === chatId
            ? {
              ...c,
              contact: {
                ...c.contact,
                firstName: res.result.firstName,
                lastName: res.result.lastName,
              },
            }
            : c
        );

        let updatedCurrentChat = state.currentChat;
        if (state.currentChat?._id === chatId) {
          updatedCurrentChat = {
            ...state.currentChat,
            contact: {
              ...state.currentChat.contact,
              firstName: res.result.firstName,
              lastName: res.result.lastName,
            },
          };
        }

        return {
          ...state,
          chats: updatedChats,
          currentChat: updatedCurrentChat,
        };
      });

      enqueueSnackbar("Контакт обновлён", { variant: "success" });
    } else {
      enqueueSnackbar("Не удалось обновить контакт", { variant: "error" });
      console.error("Ошибка ответа при обновлении контакта:", res);
    }
  } catch (err) {
    console.error("Ошибка обновления контакта:", err);
    enqueueSnackbar("Ошибка при обновлении контакта", { variant: "error" });
    throw err;
  }
};

export const handleUpdateChatsReadStatus = async (
  context: ChatContext,
  chatIds: string[],
  isUnread: boolean
): Promise<void> => {
  const { getState, setState } = context;

  try {
    const res = await updateChatsReadStatus(chatIds, isUnread);

    if (res.success) {
      setState((state) => ({
        chats: state.chats.map((locChat) => {
          const updatedChat = res.result.find(
            (srvChat) => srvChat._id === locChat._id
          );
          return updatedChat ? updatedChat : locChat;
        }),
      }));
    } else {
      enqueueSnackbar("Не удалось обновить статус чатов", {
        variant: "error",
      });
      console.error("err update read status", res);
    }
  } catch (e) {
    console.error("err update read status", e);
    enqueueSnackbar("Ошибка при обновлении статуса чатов", {
      variant: "error",
    });
  }
};
